import config from './config.js';
import { Food } from './food.js';
import { Virus } from './virus.js';
import { SaasBlob } from './saasBlob.js';
import global from './global.js';
import { generateRandomPastelColor } from './colorUtils.js';
import { Powerup } from './powerup.js';
import { updateMobileCamera } from './app.js';

export class Player {
    #name;  // private field
    maxScore = 20000;

    constructor(id, name, x, y) {
        this.id = id;
        this.#name = name || '';
        this.x = x;
        this.y = y;
        this.mass = config.defaultPlayerMass;
        this.radius = this.calculateRadius();
        this.cloudColor = this.generateRandomPastelColor();
        this.cells = [{ x, y, mass: this.mass, radius: this.radius }]; // Initialize cells array
        this.collectedSaas = [];
        this.score = 0;
        this.speed = 3; // Adjust this value to change the player's base speed
        this.speedBoost = 1; // Default speed boost multiplier
        this.speedBoostTimeout = null; // To store the timeout for resetting speed
        this.speedMultiplier = 1;
        this.slowEffect = 1;
        this.collidedViruses = new Set(); // To track collided viruses
        this.level = 1;
        this.gameStartTime = Date.now();
    }

    get name() {
        return this.#name;
    }

    updatePosition(x, y) {
        this.x = x;
        this.y = y;

        // Keep the player within the game boundaries
        this.x = Math.max(0, Math.min(this.x, config.gameWidth));
        this.y = Math.max(0, Math.min(this.y, config.gameHeight));

        this.cells.forEach(cell => {
            cell.x = this.x;
            cell.y = this.y;
        });

    }

    calculateRadius() {
        return Math.sqrt(this.mass / Math.PI) * 10;
    }

    generateRandomPastelColor() {
        const hue = Math.floor(Math.random() * 360);
        return `hsl(${hue}, 70%, 80%)`;
    }

    checkCollision(object) {
        const dx = this.x - object.x;
        const dy = this.y - object.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const overlapThreshold = (this.radius + object.radius) / 2;
        
        if (distance < this.radius + object.radius) {
            if (object instanceof Food && distance < overlapThreshold) {
                this.eatFood(object);
                return true;
            } else if (object instanceof Virus && distance < overlapThreshold) {
                this.handleVirusCollision(object);
                return true;
            } else if (object instanceof SaasBlob) {
                if (distance < overlapThreshold) {
                    if (this.radius > object.radius) {
                        this.collectSaasBlob(object);
                        return true;
                    } else {
                        return 'gameOver';
                    }
                }
            } else if (object instanceof Powerup && distance < overlapThreshold) {
                this.applySpeedBoost(object);
                return true;
            }
        }
        return false;
    }

    eatFood(food) {
        if (this.score < this.maxScore) {
            const massGain = Math.min(food.mass, this.maxScore - this.score);
            this.updateScore(massGain);
        }
    }

    handleVirusCollision(virus) {
        if (!this.collidedViruses.has(virus.id)) {
            this.applySlowEffect(2000); // Slow down for 2 seconds
            this.collidedViruses.add(virus.id);
            
            // Remove the virus from the set after 2 seconds
            setTimeout(() => {
                this.collidedViruses.delete(virus.id);
            }, 2000);
        }
    }

    collectSaasBlob(saasBlob) {
        if (!this.collectedSaas.includes(saasBlob.saasInfo.saasname)) {
            if (this.score < this.maxScore) {
                const massGain = Math.min(saasBlob.mass, this.maxScore - this.score);
                this.updateScore(massGain * 2); // SaaS blobs are worth more points
            }
            this.collectedSaas.push(saasBlob.saasInfo.saasname);
        }
        if (global.isMobile) {
            console.log("Calling updateMobileCamera after collecting SaasBlob");
            updateMobileCamera();
        }
    }

    split(numberOfSplits = 2) {
        if (this.cells.length < config.limitSplit) {
            const splitMass = this.mass / (numberOfSplits + 1);
            const splitRadius = this.calculateRadiusFromMass(splitMass);

            // Update the main cell
            this.cells[0].mass = splitMass;
            this.cells[0].radius = splitRadius;

            // Create new cells
            for (let i = 0; i < numberOfSplits; i++) {
                const angle = (Math.PI * 2 / numberOfSplits) * i;
                const splitDistance = this.radius * 2;

                const newCell = {
                    x: this.x + Math.cos(angle) * splitDistance,
                    y: this.y + Math.sin(angle) * splitDistance,
                    mass: splitMass,
                    radius: splitRadius,
                    dx: Math.cos(angle) * 20,
                    dy: Math.sin(angle) * 20
                };

                this.cells.push(newCell);
            }

            this.updateSize();
        }
    }

    mergeCells() {
        if (this.cells.length > 1) {
            const mainCell = this.cells[0];
            for (let i = 1; i < this.cells.length; i++) {
                const cell = this.cells[i];
                const dx = mainCell.x - cell.x;
                const dy = mainCell.y - cell.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < mainCell.radius + cell.radius) {
                    mainCell.mass += cell.mass;
                    this.cells.splice(i, 1);
                    i--;
                } else {
                    cell.x += dx * 0.05;
                    cell.y += dy * 0.05;
                }
            }
            this.mass = this.cells.reduce((total, cell) => total + cell.mass, 0);
            this.updateSize();
        }
    }

    // feed() {
    //     if (this.mass > config.defaultPlayerMass * 1.1) {
    //         const ejectedMass = this.mass * 0.1;
    //         this.mass -= ejectedMass;
            
    //         const angle = Math.atan2(global.target.y - global.screen.height/2, global.target.x - global.screen.width/2);
    //         const ejectedFood = new Food(
    //             this.x + Math.cos(angle) * this.radius,
    //             this.y + Math.sin(angle) * this.radius,
    //             ejectedMass
    //         );
            
    //         this.updateSize();
    //         return ejectedFood;
    //     }
    //     return null;
    // }

    updateSize() {
        this.radius = this.calculateRadius();
        this.cells.forEach(cell => {
            cell.radius = this.calculateRadiusFromMass(cell.mass);
        });
    }

    calculateRadiusFromMass(mass) {
        return Math.sqrt(mass / Math.PI) * 10;
    }

    fireFood() {
        return this.feed(); // We can use the feed method for firing food as well
    }

    applySpeedBoost(powerup) {
        this.speedBoost = powerup.speedBoost;
        
        // Clear existing timeout if there's one
        if (this.speedBoostTimeout) {
            clearTimeout(this.speedBoostTimeout);
        }

        // Set a new timeout to reset speed after the powerup duration
        this.speedBoostTimeout = setTimeout(() => {
            this.speedBoost = 1;
            this.speedBoostTimeout = null;
        }, powerup.duration);
    }

    setSpeed(newSpeed) {
        this.speed = newSpeed * this.speedMultiplier * this.slowEffect;
    }

    updateScore(amount) {
        this.score = Math.min(this.score + amount, this.maxScore);
        this.mass = Math.min(this.mass + amount, this.maxScore);
        this.updateSize();
    }

    applySlowEffect(duration) {
        this.slowEffect = 0.55; // Slow down to 85% speed (subtle effect)
        setTimeout(() => {
            this.slowEffect = 1; // Reset to normal speed after duration
        }, duration);
    }
}
